import type { VAutoItem, VAutoState, StateTree } from '@makemydeal/dr-dash-types';
import * as tradeInsSelectors from '../selectors/tradeIns';

/**
 * Normalizes a date string by removing the trailing 'Z' suffix.
 * This ensures consistent lexicographic comparison between dates with and without timezone indicators.
 * @param dateString - date string (e.g., "2026-02-16T14:47:35.453Z" or "2026-02-16T14:49:15.453625")
 * @returns Normalized date string without 'Z' suffix
 */
const normalizeDateString = (dateString: string): string => dateString.replace(/Z$/, '');

/**
 * Sort comparator for VAutoItem by updatedOn date (descending - most recent first)
 */
const sortByUpdatedOnDesc = (a: VAutoItem, b: VAutoItem): number =>
    new Date(b.updatedOn).getTime() - new Date(a.updatedOn).getTime();

/**
 * Determines if vAuto ACV should override deal API ACV based on date comparison:
 * @param isTradeAcvOverrideEnabled - Whether trade ACV override is enabled (controls if ACV field is editable)
 * @param createdOn - The offer.createdOn date (deal version creation date) from getOfferCreationDateString
 * @param appraisalUpdatedOn - The vAuto appraisal updatedOn date
 * @param appraisalValue - The vAuto appraisal value
 * @param dealAcv - The current deal ACV value
 */
export const shouldOverrideDealAcvWithVAutoAppraisal = (
    isTradeAcvOverrideEnabled: boolean,
    createdOn: string | undefined,
    appraisalUpdatedOn: string | undefined,
    appraisalValue: number,
    dealAcv: number
): boolean => {
    if (!isTradeAcvOverrideEnabled) {
        return true;
    }

    if (!appraisalValue || appraisalValue <= 0) {
        return false;
    }

    if (!dealAcv) {
        return true;
    }

    if (createdOn && appraisalUpdatedOn) {
        const dealDate = normalizeDateString(createdOn);
        const appraisalDate = normalizeDateString(appraisalUpdatedOn);

        return appraisalDate > dealDate;
    }

    if (!createdOn && appraisalUpdatedOn) {
        return true;
    }

    return false;
};

export const getCurrentAppraisalFromAppraisals = (items: VAutoItem[]) => {
    const appraisals = [...(items || [])];
    return appraisals.sort(sortByUpdatedOnDesc)[0];
};

export const getCurrentAppraisalIdFromAppraisals = (vAutoAppraisals: VAutoState) => {
    const appraisal = getCurrentAppraisalFromAppraisals(vAutoAppraisals?.items);
    return appraisal?.id;
};

/**
 * Maps each trade-in to its corresponding vAuto appraisal
 * Used by both vAutoMiddleware and vAutoTriforceMiddleware when multi-trade-in is enabled
 */
export const mapTradeInsToAppraisals = (
    state: StateTree,
    vAutoAppraisal: VAutoItem[]
): Array<{ vin: string; appraisal: VAutoItem | undefined; index: number }> => {
    const allTradeins = tradeInsSelectors.getAllTradeIns(state);

    return allTradeins.map((tradeIn, index) => {
        const vin = tradeIn?.tradeIn?.vin;
        if (!vin) {
            return { vin: '', appraisal: undefined, index };
        }
        const filteredAppraisals = vAutoAppraisal.filter((appraisal) => appraisal?.vehicle?.vin === vin);
        const appraisal = getCurrentAppraisalFromAppraisals(filteredAppraisals);
        return { vin, appraisal, index };
    });
};
